/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, Text, View } from 'react-native';
import { ticketColors } from '../../shared/theme/ticketColors'
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import { spacing } from '../../shared/theme/spacing';
import { styles } from './ReportsScreen.styles';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import type { ReportsScreenViewModel } from './useReportsScreen';
import { useReportsAgenda } from './hooks/useReportsAgenda';
import { ReportsBlockHeading } from './components/ReportsBlockHeading';
import { ReportsPeriodFilterPanel } from './components/ReportsPeriodFilterPanel';
import { formatMoney as formatCurrency } from '../../shared/format/public-api';

function formatMinutes(m: number): string {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
}

export function ReportsAgendaView({ vm }: { vm: ReportsScreenViewModel }) {
  const { rows, totals, loading, error, reload } = useReportsAgenda({
    currentBranchId: vm.currentBranchId,
    dateRangePreset: vm.dateRangePreset,
    customRange: vm.customRangeParam,
    shouldLoad: vm.isAgendaSection,
  });

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
    >
      <ReportsPeriodFilterPanel vm={vm} presetAccessibilityPrefix="Período agenda" />

      <ReportsBlockHeading label="Agenda por profesional" />

      {loading && rows.length === 0 ? (
        <View style={agendaStyles.loading}>
          <ActivityIndicator size="small" color={ticketColors.gold} />
        </View>
      ) : error ? (
        <ModuleEmptyState
          variant="nest"
          title="No se pudo cargar"
          subtitle={error}
          primaryAction={{ label: 'Reintentar', onPress: () => void reload() }}
        />
      ) : rows.length === 0 ? (
        <ModuleEmptyState variant="nest" title="Sin turnos" subtitle="No hay turnos en este período." />
      ) : (
        <PaperSheet padded testID="informes.list.sheet">
          <View style={agendaStyles.totalRow}>
            <Text style={agendaStyles.totalLabel}>Turnos completados</Text>
            <Text style={agendaStyles.totalValue}>{totals.completed}</Text>
          </View>
          <View style={agendaStyles.totalRow}>
            <Text style={agendaStyles.totalLabel}>Total facturado</Text>
            <Text style={agendaStyles.totalValue}>{formatCurrency(totals.amount)}</Text>
          </View>
          <View style={agendaStyles.totalRow}>
            <Text style={agendaStyles.totalLabel}>Horas agendadas</Text>
            <Text style={agendaStyles.totalValue}>{formatMinutes(totals.minutes)}</Text>
          </View>

          <View style={agendaStyles.divider} />

          {rows.map((row, idx) => (
            <View key={row.resourceId} style={[agendaStyles.row, idx > 0 && agendaStyles.rowBorder]}>
              <View style={agendaStyles.rowMain}>
                <Text style={agendaStyles.name}>{row.employeeName || row.resourceName}</Text>
                <Text style={agendaStyles.amount}>{formatCurrency(row.totalAmount)}</Text>
              </View>
              <View style={agendaStyles.rowMeta}>
                <Text style={agendaStyles.meta}>
                  {row.completedCount} {row.completedCount === 1 ? 'completado' : 'completados'} · {row.noShowCount}{' '}
                  {row.noShowCount === 1 ? 'no vino' : 'no vinieron'} · {row.cancelledCount}{' '}
                  {row.cancelledCount === 1 ? 'cancelado' : 'cancelados'}
                </Text>
                <Text style={agendaStyles.meta}>{formatMinutes(row.totalMinutes)}</Text>
              </View>
            </View>
          ))}
        </PaperSheet>
      )}
    </ScrollView>
  );
}

const agendaStyles = StyleSheet.create({
  loading: { padding: spacing.lg, alignItems: 'center' },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  totalLabel: {
    color: ticketColors.ink2,
    fontSize: 14,
  },
  totalValue: {
    color: ticketColors.ink,
    fontSize: 16,
    fontWeight: '700',
  },
  divider: {
    height: 1,
    backgroundColor: ticketColors.ruleSoft,
    marginVertical: spacing.sm,
  },
  row: {
    paddingVertical: spacing.sm,
  },
  rowBorder: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  rowMain: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rowMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 2,
  },
  name: {
    color: ticketColors.ink,
    fontSize: 15,
    fontWeight: '600',
  },
  amount: {
    color: ticketColors.ink,
    fontSize: 15,
    fontWeight: '700',
  },
  meta: {
    color: ticketColors.ink3,
    fontSize: 12,
  },
});
